import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Download, Check, Mail, Phone, X, Send } from "lucide-react";
import { toast, Toaster } from "sonner";

const Confirmation = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { id } = useParams();
  const [params] = useSearchParams();
  const fallbackNumber = params.get("n") || id;
  const [doc, setDoc] = useState(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/contracts/${id}/summary`)
      .then(({ data }) => setDoc(data))
      .catch(() => setDoc(null));
  }, [id]);

  const number = doc?.contract_number || fallbackNumber;
  const isStaff = user?.role === "admin" || user?.role === "station_manager";
  const isCustomerOwn = user?.role === "customer";
  const showPdf = isStaff || isCustomerOwn;
  const customerHasEmail = !!doc?.customer_email;

  const handleDownload = async () => {
    const endpoint = isCustomerOwn ? `/my/contracts/${id}/pdf` : `/contracts/${id}/pdf`;
    try {
      const res = await api.get(endpoint, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("PDF-Download fehlgeschlagen");
    }
  };

  const sendEmail = async () => {
    const email = (emailInput || "").trim();
    if (!email || !email.includes("@")) {
      toast.error("Bitte gültige E-Mail-Adresse eingeben.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/contracts/${id}/email`, { email });
      if (data.ok) {
        toast.success(`Bestätigung an ${email} gesendet`);
        setEmailModalOpen(false);
      } else {
        toast.error(data.message || "Versand fehlgeschlagen");
      }
    } catch (e) {
      const status = e?.response?.status;
      toast.error(status === 429 ? "Zu viele Anfragen — bitte später erneut versuchen." : "Versand fehlgeschlagen");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rs-app min-h-screen bg-white">
      <Header />
      <Toaster position="top-center" />
      <div className="max-w-xl mx-auto px-6 py-16 sm:py-24">
        {/* Hero check */}
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-[var(--rs-primary)] flex items-center justify-center text-white rounded-full">
            <Check size={36} strokeWidth={3} />
          </div>
          <h1 className="font-[Chivo] text-4xl sm:text-5xl font-black tracking-tight mt-6 leading-[1]">
            {t("confirmation_title")}
          </h1>
          <p className="text-[var(--rs-text-muted)] mt-3">
            {customerHasEmail
              ? t("confirmation_sent_email").replace("{email}", doc.customer_email)
              : t("confirmation_no_email")}
          </p>
        </div>

        {/* Contract number — primary focus */}
        <div className="mt-10 border-2 border-[var(--rs-text)] p-6 text-center">
          <div className="rs-overline">{t("contract_number")}</div>
          <div className="font-[Chivo] text-3xl sm:text-4xl font-black tracking-tight mt-1" data-testid="confirmation-number">
            {number}
          </div>
        </div>

        {/* Primary actions */}
        <div className="mt-6 flex flex-col gap-3">
          {showPdf && (
            <button type="button" className="rs-btn-primary justify-center" onClick={handleDownload} data-testid="confirmation-pdf-btn">
              <Download size={16} className="mr-2" />
              {t("download_pdf")}
            </button>
          )}
          {!customerHasEmail && (
            <button
              type="button"
              className="rs-btn-secondary justify-center"
              onClick={() => { setEmailInput(""); setEmailModalOpen(true); }}
              data-testid="confirmation-email-btn"
            >
              <Send size={16} className="mr-2" />
              {t("send_by_email")}
            </button>
          )}
        </div>

        {/* Help contact — single line */}
        <div className="mt-8 border-t border-[var(--rs-border)] pt-6 text-center text-sm text-[var(--rs-text-muted)]">
          {t("confirmation_help")}{" "}
          <a href="tel:+41793966660" className="font-bold text-[var(--rs-primary)] underline whitespace-nowrap">
            <Phone size={12} className="inline mr-1" />079 396 66 60
          </a>
        </div>

        <div className="mt-10 text-center">
          <Link to="/" className="text-xs uppercase tracking-[0.2em] text-[var(--rs-text-muted)] hover:text-[var(--rs-primary)]" data-testid="confirmation-home-btn">
            ← {t("back_to_home")}
          </Link>
        </div>

        {/* Email modal */}
        {emailModalOpen && (
          <div
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            onClick={() => !sending && setEmailModalOpen(false)}
            data-testid="email-modal"
          >
            <div className="bg-white border-4 border-[var(--rs-primary)] w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-5 border-b border-[var(--rs-border)] bg-[var(--rs-primary)] text-white">
                <div>
                  <div className="rs-overline !text-white/60">{number}</div>
                  <h3 className="font-[Chivo] text-2xl font-black mt-1">{t("send_by_email")}</h3>
                </div>
                <button type="button" onClick={() => !sending && setEmailModalOpen(false)} className="text-white/70 hover:text-white" data-testid="email-modal-close">
                  <X size={20} />
                </button>
              </div>
              <div className="p-5">
                <label className="block">
                  <span className="rs-label">{t("f_email")}</span>
                  <input
                    type="email"
                    autoFocus
                    className="rs-input"
                    placeholder="ihre@email.ch"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !sending && sendEmail()}
                    data-testid="email-modal-input"
                  />
                </label>
              </div>
              <div className="p-5 border-t border-[var(--rs-border)] flex justify-between gap-3">
                <button type="button" className="rs-btn-secondary !py-3 !text-xs" onClick={() => !sending && setEmailModalOpen(false)} disabled={sending} data-testid="email-modal-cancel">
                  {t("btn_cancel")}
                </button>
                <button type="button" className="rs-btn-primary !py-3 !text-xs disabled:opacity-50" onClick={sendEmail} disabled={sending || !emailInput.includes("@")} data-testid="email-modal-send">
                  <Mail size={14} className="mr-2" />
                  {sending ? t("saving") : t("btn_send")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Confirmation;
